import {
  Box,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingSkeleton, GeneralTableBox, HeadTableCell, RowTableCell } from '../../../../common/components';
import GerencialesPieTableSection from '../GerencialesPieTableSection';
import { PIE_ALLOWED_COLORS } from '../../constants/gerencialesConstants';
import { formatCurrency } from '../../../../utils/formatUtils';

const toPieData = (rows) =>
  rows
    .filter((r) => r.count > 0)
    .map((r, i) => ({
      name: r.label,
      value: r.count,
      fill: PIE_ALLOWED_COLORS[i % PIE_ALLOWED_COLORS.length],
    }));

const AnniversaryPlanReport = ({ loading, results, header }) => {
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!results) {
    return null;
  }

  const totalContracts = header?.total_contracts ?? 0;
  const totalOverdueAmount = header?.total_overdue_amount ?? 0;

  const debtorsPie = toPieData(results.debtors ?? []);
  const pendingPie = toPieData(results.pending_ranges ?? []);
  const progressPie = toPieData(results.payment_progress ?? []);
  const topDebtors = results.top_debtors ?? [];

  return (
    <Box>
      {/* Summary header */}
      <Paper elevation={1} sx={{ mb: 1, mt: 1, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Contratos en plan</Typography>
            <Typography variant="h6" fontWeight="bold">{totalContracts}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Deuda mora total</Typography>
            <Typography variant="h6" fontWeight="bold">{formatCurrency(totalOverdueAmount)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* 1. Deudores vs no deudores */}
      <GerencialesPieTableSection
        title="Deudores vs no deudores"
        columns={[
          { key: 'label', label: 'Estado', align: 'left' },
          { key: 'count', label: 'Contratos', align: 'center' },
        ]}
        rows={results.debtors ?? []}
        pieData={debtorsPie}
        totalLabel="Total"
        totalCount={totalContracts}
        chartId="ann-debtors-pie"
      />

      {/* 2. Cuotas restantes por rango */}
      <GerencialesPieTableSection
        title="Cuotas restantes para finalizar el acuerdo"
        columns={[
          { key: 'label', label: 'Rango', align: 'left' },
          { key: 'count', label: 'Contratos', align: 'center' },
        ]}
        rows={results.pending_ranges ?? []}
        pieData={pendingPie}
        totalLabel="Total"
        totalCount={totalContracts}
        chartId="ann-pending-pie"
      />

      {/* 3. Progreso de pago */}
      <GerencialesPieTableSection
        title="Progreso de pago (cuotas pagadas / pactadas)"
        columns={[
          { key: 'label', label: 'Progreso', align: 'left' },
          { key: 'count', label: 'Contratos', align: 'center' },
        ]}
        rows={results.payment_progress ?? []}
        pieData={progressPie}
        totalLabel="Total"
        totalCount={totalContracts}
        chartId="ann-progress-pie"
      />

      {/* 4. Top 10 mayores deudores */}
      {topDebtors.length > 0 && (
        <Paper elevation={1} sx={{ mb: 1, mt: 1 }}>
          <Box
            sx={{
              backgroundColor: '#3b3f46',
              color: 'white',
              px: 2.5,
              py: 1.5,
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Top 10 mayores deudores
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <GeneralTableBox>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell>Contrato</HeadTableCell>
                    <HeadTableCell>Suscriptor</HeadTableCell>
                    <HeadTableCell align="center">Cuotas vencidas</HeadTableCell>
                    <HeadTableCell align="center">Cuotas restantes</HeadTableCell>
                    <HeadTableCell align="right">Importe mora</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topDebtors.map((row, i) => (
                    <TableRow key={`${row.contract_number}-${i}`} hover>
                      <RowTableCell>{row.contract_number}</RowTableCell>
                      <RowTableCell sx={{ maxWidth: 220 }}>
                        {row.subscriber_names?.length > 0
                          ? row.subscriber_names.map((name, j) => (
                              <Typography key={j} variant="body2" fontSize={12}>
                                {name}
                              </Typography>
                            ))
                          : <Typography variant="body2">Sin suscriptor</Typography>}
                      </RowTableCell>
                      <RowTableCell align="center">{row.overdue_installments}</RowTableCell>
                      <RowTableCell align="center">{row.pending_installments}</RowTableCell>
                      <RowTableCell align="right">{formatCurrency(row.overdue_amount)}</RowTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </GeneralTableBox>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AnniversaryPlanReport;
